import Auth from '/imports/ui/services/auth';
import PollService from '/imports/ui/components/poll/service';
import { defineMessages } from 'react-intl';
import { notify } from '/imports/ui/services/notification';
import caseInsensitiveReducer from '/imports/utils/caseInsensitiveReducer';

import { partition } from '/imports/utils/array-utils';

import { uniqueId } from '@bigbluebutton/tldraw';

import { v4 as uuid } from 'uuid';

import logger from '/imports/startup/client/logger';

import { requestPresentationUploadTokenMutation } from './mutation';

import apolloContextHolder from '../../core/graphql/apolloContextHolder/apolloContextHolder';

import { getPresentationUploadToken } from './queries';

import update from 'immutability-helper';

import Compressor from "compressorjs";

import PresentationUploaderService from '/imports/ui/components/presentation/presentation-uploader/service';

const { backupPresentationToGCS } = PresentationUploaderService;
const POD_ID = 'DEFAULT_PRESENTATION_POD';

const TOKEN_TIMEOUT = 5000;

const intlMessages = defineMessages({
  notifyNotAllowedChange: {
    id: 'app.whiteboard.annotations.notAllowed',
    description: 'Label shown in toast when the user make a change on a shape he doesnt have permission',
  },
  shapeNumberExceeded: {
    id: 'app.whiteboard.annotations.numberExceeded',
    description: 'Label shown in toast when the user tries to add more shapes than the limit',
  },
  shapeNumberWarning: {
    id: 'app.whiteboard.annotations.numberWarning',
    description: 'Label shown in toast when annotations approach the limit, suggesting a new slide',
  },
});

const annotationsQueue = [];
// How many packets we need to have to use annotationsBufferTimeMax
const annotationsMaxDelayQueueSize = 60;
// Minimum bufferTime
const annotationsBufferTimeMin = 30;
// Maximum bufferTime
const annotationsBufferTimeMax = 200;
// Time before running 'sendBulkAnnotations' again if user is offline
const annotationsRetryDelay = 1000;

let annotationsSenderIsRunning = false;

const proccessAnnotationsQueue = async (submitAnnotations) => {
  annotationsSenderIsRunning = true;
  const queueSize = annotationsQueue.length;

  if (!queueSize) {
    annotationsSenderIsRunning = false;
    return;
  }

  const annotations = annotationsQueue.splice(0, queueSize);

  try {
    const isAnnotationSent = await submitAnnotations(annotations);

    if (!isAnnotationSent) {
      // undo splice
      annotationsQueue.splice(0, 0, ...annotations);
      setTimeout(() => proccessAnnotationsQueue(submitAnnotations), annotationsRetryDelay);
    } else {
      // ask tiago
      const delayPerc = Math.min(
        annotationsMaxDelayQueueSize, queueSize,
      ) / annotationsMaxDelayQueueSize;
      const delayDelta = annotationsBufferTimeMax - annotationsBufferTimeMin;
      const delayTime = annotationsBufferTimeMin + delayDelta * delayPerc;
      setTimeout(() => proccessAnnotationsQueue(submitAnnotations), delayTime);
    }
  } catch (error) {
    annotationsQueue.splice(0, 0, ...annotations);
    setTimeout(() => proccessAnnotationsQueue(submitAnnotations), annotationsRetryDelay);
  }
};

const sendAnnotation = (annotation, submitAnnotations) => {
  // Prevent sending annotations while disconnected
  // TODO: Change this to add the annotation, but delay the send until we're
  // reconnected. With this it will miss things

  const index = annotationsQueue.findIndex((ann) => ann.id === annotation.id);
  if (index !== -1) {
    annotationsQueue[index] = annotation;
  } else {
    annotationsQueue.push(annotation);
  }
  if (!annotationsSenderIsRunning) {
    setTimeout(() => proccessAnnotationsQueue(submitAnnotations), annotationsBufferTimeMin);
  }
};

const persistShape = async (shape, whiteboardId, isModerator, submitAnnotations) => {
  const annotation = {
    id: shape.id,
    annotationInfo: { ...shape, isModerator },
    wbId: whiteboardId,
    userId: Auth.userID,
  };

  sendAnnotation(annotation, submitAnnotations);
};

const initDefaultPages = (count = 1) => {
  const pages = {};
  const pageStates = {};
  let i = 0;
  while (i < count + 1) {
    pages[`${i}`] = {
      id: `${i}`,
      name: `Slide ${i}`,
      shapes: {},
      bindings: {},
    };
    pageStates[`${i}`] = {
      id: `${i}`,
      selectedIds: [],
      camera: {
        point: [0, 0],
        zoom: 1,
      },
    };
    i += 1;
  }
  return { pages, pageStates };
};

const notifyNotAllowedChange = (intl) => {
  console.log('Image error ')
  if (intl) notify(intl.formatMessage(intlMessages.notifyNotAllowedChange), 'warning', 'whiteboard');
};

const notifyShapeNumberExceeded = (intl, limit) => {
  if (intl) notify(intl.formatMessage(intlMessages.shapeNumberExceeded, { 0: limit }), 'warning', 'whiteboard');
};

const notifyShapeNumberWarning = (intl, threshold) => {
  if (intl) notify(intl.formatMessage(intlMessages.shapeNumberWarning, { 0: threshold }), 'warning', 'whiteboard');
};

const toggleToolsAnimations = (activeAnim, anim, time, hasWBAccess = false) => {
  const handleOptionsDropdown = () => {
    const optionsDropdown = document.getElementById('WhiteboardOptionButton');
    if (optionsDropdown) {
      optionsDropdown.classList.remove(activeAnim);
      optionsDropdown.style.transition = `opacity ${time} ease-in-out`;
      optionsDropdown.classList.add(anim);
    }
  };

  if (hasWBAccess === false) {
    return handleOptionsDropdown();
  }

  const checkElementsAndRun = () => {
    const tlEls = document.querySelectorAll('.tlui-menu-zone, .tlui-toolbar__tools, .tlui-toolbar__extras, .tlui-style-panel__wrapper');
    if (tlEls.length) {
      tlEls?.forEach((el) => {
        el.classList.remove(activeAnim);
        // eslint-disable-next-line no-param-reassign
        el.style.transition = `opacity ${time} ease-in-out`;
        el.classList.add(anim);
      });
      handleOptionsDropdown();
    } else {
      // If the elements are not yet in the DOM, wait for 50ms and try again
      setTimeout(checkElementsAndRun, 300);
    }
  };

  return checkElementsAndRun();
};

const formatAnnotations = (annotations, intl, curPageId, currentPresentationPage) => {
  const result = {};

  annotations.forEach((annotation) => {
    if (!annotation.annotationInfo) return;

    let annotationInfo = annotation.annotationInfo;

    if (annotationInfo.questionType) {
      // poll result, convert it to text and create tldraw shape
      if (!annotationInfo.props) {
        annotationInfo.answers = annotationInfo.answers.reduce(
          caseInsensitiveReducer, [],
        );
        const pollResult = PollService.getPollResultString(annotationInfo, intl)
          .split('<br/>').join('\n').replace(/(<([^>]+)>)/ig, '');

        const lines = pollResult.split('\n');
        const longestLine = lines.reduce((a, b) => (a.length > b.length ? a : b), '').length;

        // Text measurement estimation
        const averageCharWidth = 14;
        const lineHeight = 32;
        const padding = 2;

        const annotationWidth = longestLine * averageCharWidth;
        const annotationHeight = lines.length * lineHeight;

        const slideWidth = currentPresentationPage?.scaledWidth;
        const slideHeight = currentPresentationPage?.scaledHeight;
        const xPosition = slideWidth - annotationWidth - padding;
        const yPosition = slideHeight - annotationHeight - padding;

        annotationInfo = {
          x: xPosition,
          y: yPosition,
          isLocked: false,
          rotation: 0,
          typeName: 'shape',
          opacity: 1,
          parentId: `page:${curPageId}`,
          index: 'a1',
          id: `${annotationInfo.id}`,
          meta: {},
          type: 'poll',
          props: {
            color: 'black',
            fill: 'semi',
            w: annotationWidth,
            h: annotationHeight,
            answers: annotationInfo.answers,
            numRespondents: annotationInfo.numRespondents,
            numResponders: annotationInfo.numResponders,
            questionText: annotationInfo.questionText,
            questionType: annotationInfo.questionType,
            question: annotationInfo.question || '',
          },
        };
      } else {
        annotationInfo = {
          x: annotationInfo.x,
          isLocked: annotationInfo.isLocked,
          y: annotationInfo.y,
          rotation: annotationInfo.rotation,
          typeName: annotationInfo.typeName,
          opacity: annotationInfo.opacity,
          parentId: annotationInfo.parentId,
          index: annotationInfo.index,
          id: annotationInfo.id,
          meta: annotationInfo.meta,
          type: 'poll',
          props: {
            color: 'black',
            fill: 'semi',
            h: annotationInfo.props.h,
            w: annotationInfo.props.w,
            answers: annotationInfo.answers,
            numRespondents: annotationInfo.numRespondents,
            numResponders: annotationInfo.numResponders,
            questionText: annotationInfo.questionText,
            questionType: annotationInfo.questionType,
            question: annotationInfo.question || '',
          },
        };
      }
      const cpg = parseInt(annotationInfo?.id?.split?.('/')?.[1], 10);
      if (cpg !== parseInt(curPageId, 10)) return;
      annotationInfo.questionType = false;
    }
    result[annotationInfo.id] = annotationInfo;
  });
  return result;
};

const requestPresentationUploadToken = (
  temporaryPresentationId,
  meetingId,
  filename,
) => new Promise((resolve, reject) => {
  const client = apolloContextHolder.getClient();

  client.mutate({
    mutation: requestPresentationUploadTokenMutation,
    variables: {
      podId: POD_ID,
      filename,
      uploadTemporaryId: temporaryPresentationId,
    },
  })
  const timeout = setTimeout(() => {
    reject(new Error({ code: 408, message: 'requestPresentationUploadToken timeout' }));
  }, TOKEN_TIMEOUT);


  const getData = (n = 0) => {
    if (n > 10) return;
    let recursiveTimeout = null
    // Track start time of this query
    const queryStartTime = Date.now();
    client.query({
      query: getPresentationUploadToken,
      variables: {
        uploadTemporaryId: temporaryPresentationId,
      },
      fetchPolicy: 'network-only',
    }).then((result) => {
      window.dispatchEvent(
        new CustomEvent("presentation-change", {
          detail: { isChangeReq: true, presentationId: null }
        })
      );
      if (result.data.pres_presentation_uploadToken.length > 0) {
        clearTimeout(recursiveTimeout);
        clearTimeout(timeout);

        const elapsed = Date.now() - queryStartTime;
        // If query took more than 2 seconds, fire event
        if (elapsed > 5000) {
          window.dispatchEvent(
            new CustomEvent("presentation-change", {
              detail: { isChangeReq: true, presentationId: null },
            })
          );
        }
        resolve(result.data.pres_presentation_uploadToken[0].uploadToken);
      }
    });
    recursiveTimeout = setTimeout(() => {
      getData(n + 1);
    }, 1000);
  };
  setTimeout(getData, 100);
});

// fetch doesn't support progress. So we use xhr which support progress.

const futch = (url, opts = {}, onProgress) => new Promise((res, rej) => {
  const xhr = new XMLHttpRequest();
  xhr.open(opts.method || 'get', url);
  Object.keys(opts.headers || {})
    .forEach((k) => xhr.setRequestHeader(k, opts.headers[k]));
  xhr.onload = (e) => {
    if (e.target.status !== 200) {
      return rej(new Error({ code: e.target.status, message: e.target.statusText }));
    }
    return res(e.target.responseText);
  };
  xhr.onerror = rej;
  if (xhr.upload && onProgress) {
    xhr.upload.addEventListener('progress', onProgress, false);
  }
  xhr.send(opts.body);
});

const uploadAndConvertPresentation = (
  filename,
  temporaryPresentationId,
  file,
  downloadable,
  meetingId,
  endpoint,
  onUpload,
  onProgress,
  onConversion,
  current,
) => {

  if (!file) return Promise.resolve();
  const data = new FormData();
  data.append('fileUpload', file);
  data.append('conference', meetingId);
  data.append('room', meetingId);
  data.append('temporaryPresentationId', temporaryPresentationId);
  // TODO: Currently the uploader is not related to a POD so the id is fixed to the default
  data.append('pod_id', POD_ID);
  data.append('is_downloadable', downloadable);
  data.append('current', current);
  const opts = {
    method: 'POST',
    body: data,
  };
  return requestPresentationUploadToken(temporaryPresentationId, meetingId, filename)
    .then((token) => futch(endpoint.replace('upload', `${token}/upload`), opts, onProgress)
    )
    .then((result) => {
      // After successful BBB upload, backup to GCS for prepare mode persistence
      backupPresentationToGCS(file, filename, current);
      return result;
    })
    // Trap the error so we can have parallel upload
    .catch((error) => {
      logger.debug({
        logCode: 'presentation_uploader_service',
        extraInfo: {
          error,
        },
      }, 'Generic presentation upload exception catcher');
      onUpload({ error: true, done: true, status: error.code });
      return Promise.resolve();
    });
};

const uploadAndConvertPresentations = (
  presentationsToUpload,
  meetingId,
  uploadEndpoint,
) => Promise.all(presentationsToUpload.map((p) => uploadAndConvertPresentation(
  p.name,
  p.presentationId, p.file, p.downloadable, meetingId, uploadEndpoint,
  p.onUpload, p.onProgress, p.onConversion, p.current,
)));

const removePresentations = (
  presentationsToRemove,
  removePresentation,
) => Promise.all(presentationsToRemove.map((p) => removePresentation(p.presentationId)));
const persistPresentationChanges = (
  oldState,
  newState,
  uploadEndpoint,
  setPresentation,
  removePresentation,
) => {
  const presentationsToUpload = newState.filter((p) => !p.uploadCompleted);
  const presentationsToRemove = oldState.filter((p) => !newState.find(
    (u) => u.presentationId === p.presentationId,
  ));
  let currentPresentation = newState.find((p) => p.current);
  return uploadAndConvertPresentations(presentationsToUpload, Auth.meetingID, uploadEndpoint)
    .then((presentations) => {
      if (!presentations.length && !currentPresentation) return Promise.resolve();
      // Update the presentation with their new ids

      presentations.forEach((p, i) => {
        if (p === undefined) return;
        presentationsToUpload[i].onDone(p.presentationId);
      });
      return Promise.resolve(presentations);
    })
    .then((presentations) => {
      if (currentPresentation === undefined) {
        // setPresentation('');
        return Promise.resolve()
      }
      // If its a newly uploaded presentation we need to get it from promise result
      if (currentPresentation?.uploadInProgress) {
        const currentIndex = presentationsToUpload.findIndex((p) => p === currentPresentation);
        currentPresentation = presentations[currentIndex];
      }
      // skip setting as current if error happened
      if (currentPresentation?.conversion?.error) {
        return Promise.resolve();
      }
      // return setPresentation(currentPresentation?.presentationId);
    })
    .then(removePresentations.bind(null, presentationsToRemove, removePresentation));
};

const handleSavePresentation = (
  presentations = [],
  isFromPresentationUploaderInterface = true,
  newPres = {},
  currentPresentations = [],
  setPresentation,
  removePresentation,
  isPresentationEnabled,
) => {
  if (!isPresentationEnabled) {
    return null;
  }
  const PRESENTATION_CONFIG = window.meetingClientSettings.public.presentation;
  let updatedPresentations = [...presentations];

  if (!isFromPresentationUploaderInterface) {
    if (updatedPresentations.length === 0) {
      updatedPresentations = [...currentPresentations];
    }
    updatedPresentations = updatedPresentations.map((p) => update(p, {
      current: {
        $set: false,
      },
    }));
    const updatedNewPres = { ...newPres, current: true }; // Avoid mutating newPres
    updatedPresentations.push(updatedNewPres);
  }
  return persistPresentationChanges(
    currentPresentations,
    updatedPresentations,
    PRESENTATION_CONFIG.uploadEndpoint,
    setPresentation,
    removePresentation,
  );
};

function handleFiledrop(
  files1,
  files2,
  presentations,
  setPresentations,
  toUploadCount,
  setToUploadCount,
  handleCurrentChange,
  intl,
  deepMergeUpdateFileKey,
  updateFileKey,
) {
  const fileValidMimeTypes = window.meetingClientSettings.public.presentation.uploadValidMimeTypes

  const validMimes = Array.isArray(fileValidMimeTypes)
    ? fileValidMimeTypes.map((fileValid) => fileValid.mime)
    : [];

  const validExtensions = Array.isArray(fileValidMimeTypes)
    ? fileValidMimeTypes.map((fileValid) => fileValid.extension)
    : [];

  const safeFiles1 = Array.isArray(files1) ? files1 : Array.from(files1 ?? []);
  const safeFiles2 = Array.isArray(files2) ? files2 : Array.from(files2 ?? []);

  const [accepted, rejected] = partition(
    [...safeFiles1, ...safeFiles2],
    (f) =>
      validMimes.includes(f.type) ||
      validExtensions.includes(`.${f.name.split('.').pop()?.toLowerCase()}`),
  );
  const presentationsToUpload = accepted.map((file) => {
    const id = uniqueId(uuid());

    return {
      file,
      downloadable: false,
      isRemovable: true,
      presentationId: id,
      name: file.name,
      current: false,
      conversion: { done: false, error: false },
      upload: { done: false, error: false, progress: 0 },
      exportation: { error: false },
      onProgress: (event) => {
        if (!event.lengthComputable) {
          deepMergeUpdateFileKey(id, 'upload', {
            progress: 100,
            done: true,
          });
          return;
        }
        deepMergeUpdateFileKey(id, 'upload', {
          progress: (event.loaded / event.total) * 100,
          done: event.loaded === event.total,
        });
      },
      onConversion: (conversion) => {
        deepMergeUpdateFileKey(id, 'conversion', conversion);
      },
      onUpload: (upload) => {
        deepMergeUpdateFileKey(id, 'upload', upload);
      },
      onDone: (newId) => {
        updateFileKey(id, 'id', newId);
      },
    };
  });
  // setPresentations((prev) => [...prev, ...presentationsToUpload]);
  const updatedPresentations = [...presentations, ...presentationsToUpload];
  // setPresentations(updatedPresentations);
  setToUploadCount((prevCount) => prevCount + presentationsToUpload.length);
  // Set the first new one as current
  if (presentationsToUpload.length > 0) {
    handleCurrentChange(presentationsToUpload[0].presentationId);
  }
  if (rejected.length > 0) {
    notify(intl.formatMessage('rejected error'), 'error');
  }
  // return what you know is latest
  return update(updatedPresentations, {
    [updatedPresentations.findIndex((p) => p.presentationId === presentationsToUpload[0].presentationId)]: {
      $apply: (p) => ({ ...p, current: true }),
    },
  });; // return new state

}

const getCustomEditorAssetUrls = () => {
  const BASENAME = window.meetingClientSettings.public.app.basename;
  const TL_TEXT_PATHS = `${BASENAME}/fonts/tldraw`;

  return {
    fonts: {
      draw: `${TL_TEXT_PATHS}/Shantell_Sans-Tldrawish.woff2`,
      serif: `${TL_TEXT_PATHS}/IBMPlexSerif-Medium.woff2`,
      sansSerif: `${TL_TEXT_PATHS}/IBMPlexSans-Medium.woff2`,
      monospace: `${TL_TEXT_PATHS}/IBMPlexMono-Medium.woff2`,
    },
  };
};

const getCustomAssetUrls = () => {
  const BASENAME = window.meetingClientSettings.public.app.basename;
  const TL_ICON_PATHS = `${BASENAME}/svgs/tldraw`;
  return {
    icons: {
      menu: `${TL_ICON_PATHS}/menu.svg`,
      undo: `${TL_ICON_PATHS}/undo.svg`,
      redo: `${TL_ICON_PATHS}/redo.svg`,
      trash: `${TL_ICON_PATHS}/trash.svg`,
      lwLogo: `${BASENAME}/resources/images/logo-mini.svg`,
      'tool-delete-all': `${TL_ICON_PATHS}/trash.svg`,
      'tool-grid': `${TL_ICON_PATHS}/grid.svg`,
      duplicate: `${TL_ICON_PATHS}/duplicate.svg`,
      unlock: `${TL_ICON_PATHS}/unlock.svg`,
      'arrowhead-none': `${TL_ICON_PATHS}/arrowhead-none.svg`,
      'arrowhead-arrow': `${TL_ICON_PATHS}/arrowhead-arrow.svg`,
      'arrowhead-triangle': `${TL_ICON_PATHS}/arrowhead-triangle.svg`,
      'arrowhead-square': `${TL_ICON_PATHS}/arrowhead-square.svg`,
      'arrowhead-dot': `${TL_ICON_PATHS}/arrowhead-dot.svg`,
      'arrowhead-diamond': `${TL_ICON_PATHS}/arrowhead-diamond.svg`,
      'arrowhead-triangle-inverted': `${TL_ICON_PATHS}/arrowhead-triangle-inverted.svg`,
      'arrowhead-bar': `${TL_ICON_PATHS}/arrowhead-bar.svg`,
      'dots-horizontal': `${TL_ICON_PATHS}/dots-horizontal.svg`,
      'dots-vertical': `${TL_ICON_PATHS}/dots-vertical.svg`,
      'tool-pointer': `${TL_ICON_PATHS}/tool-pointer.svg`,
      // 'tool-media': `${TL_ICON_PATHS}/tool-media.svg`,
      // 'tool-media' : `${TL_ICON_PATHS}/upload-icon.svg`,
      'tool-media': `${TL_ICON_PATHS}/image-tool.svg`,
      'tool-hand': `${TL_ICON_PATHS}/tool-hand.svg`,
      'tool-pencil': `${TL_ICON_PATHS}/tool-pencil.svg`,
      'tool-eraser': `${TL_ICON_PATHS}/tool-eraser.svg`,
      'tool-arrow': `${TL_ICON_PATHS}/tool-arrow.svg`,
      'tool-text': `${TL_ICON_PATHS}/tool-text.svg`,
      'tool-laser': `${TL_ICON_PATHS}/tool-laser.svg`,
      'tool-note': `${TL_ICON_PATHS}/tool-note.svg`,
      'tool-line': `${TL_ICON_PATHS}/tool-line.svg`,
      'tool-highlight': `${TL_ICON_PATHS}/tool-highlight.svg`,
      'tool-frame': `${TL_ICON_PATHS}/tool-frame.svg`,
      // 'chevron-up': `${TL_ICON_PATHS}/chevron-up.svg`,
      'chevron-up': `${TL_ICON_PATHS}/toolsIcon.svg`,
      'chevron-down': `${TL_ICON_PATHS}/chevron-down.svg`,
      'chevron-right': `${TL_ICON_PATHS}/chevron-right.svg`,
      blob: `${TL_ICON_PATHS}/blob.svg`,
      // 'tool-icon':`${TL_ICON_PATHS}toolsIcon.svg`,
      'geo-rectangle': `${TL_ICON_PATHS}/geo-rectangle.svg`,
      'geo-ellipse': `${TL_ICON_PATHS}/geo-ellipse.svg`,
      'geo-diamond': `${TL_ICON_PATHS}/geo-diamond.svg`,
      'geo-triangle': `${TL_ICON_PATHS}/geo-triangle.svg`,
      'geo-trapezoid': `${TL_ICON_PATHS}/geo-trapezoid.svg`,
      'geo-rhombus': `${TL_ICON_PATHS}/geo-rhombus.svg`,
      'geo-rhombus-2': `${TL_ICON_PATHS}/geo-rhombus-2.svg`,
      'geo-pentagon': `${TL_ICON_PATHS}/geo-pentagon.svg`,
      'geo-octagon': `${TL_ICON_PATHS}/geo-octagon.svg`,
      'geo-hexagon': `${TL_ICON_PATHS}/geo-hexagon.svg`,
      'geo-cloud': `${TL_ICON_PATHS}/geo-cloud.svg`,
      'geo-star': `${TL_ICON_PATHS}/geo-star.svg`,
      'geo-oval': `${TL_ICON_PATHS}/geo-oval.svg`,
      'geo-x-box': `${TL_ICON_PATHS}/geo-x-box.svg`,
      'geo-check-box': `${TL_ICON_PATHS}/geo-check-box.svg`,
      'geo-arrow-left': `${TL_ICON_PATHS}/geo-arrow-left.svg`,
      'geo-arrow-up': `${TL_ICON_PATHS}/geo-arrow-up.svg`,
      'geo-arrow-down': `${TL_ICON_PATHS}/geo-arrow-down.svg`,
      'geo-arrow-right': `${TL_ICON_PATHS}/geo-arrow-right.svg`,
      'align-left': `${TL_ICON_PATHS}/align-left.svg`,
      'align-top': `${TL_ICON_PATHS}/align-top.svg`,
      'arrow-left': `${TL_ICON_PATHS}/arrow-left.svg`,
      'align-right': `${TL_ICON_PATHS}/align-right.svg`,
      'align-center-horizontal': `${TL_ICON_PATHS}/align-center-horizontal.svg`,
      'align-bottom': `${TL_ICON_PATHS}/align-bottom.svg`,
      'align-center-vertical': `${TL_ICON_PATHS}/align-center-vertical.svg`,
      'stretch-vertical': `${TL_ICON_PATHS}/stretch-vertical.svg`,
      'stretch-horizontal': `${TL_ICON_PATHS}/stretch-horizontal.svg`,
      'distribute-horizontal': `${TL_ICON_PATHS}/distribute-horizontal.svg`,
      'distribute-vertical': `${TL_ICON_PATHS}/distribute-vertical.svg`,
      'stack-horizontal': `${TL_ICON_PATHS}/stack-horizontal.svg`,
      'stack-vertical': `${TL_ICON_PATHS}/stack-vertical.svg`,
      'send-to-back': `${TL_ICON_PATHS}/send-to-back.svg`,
      'send-backward': `${TL_ICON_PATHS}/send-backward.svg`,
      'bring-forward': `${TL_ICON_PATHS}/bring-forward.svg`,
      'bring-to-front': `${TL_ICON_PATHS}/bring-to-front.svg`,
      'reset-zoom': `${TL_ICON_PATHS}/reset-zoom.svg`,
      'rotate-cw': `${TL_ICON_PATHS}/rotate-cw.svg`,
      link: `${TL_ICON_PATHS}/link.svg`,
      group: `${TL_ICON_PATHS}/group.svg`,
      color: `${TL_ICON_PATHS}/color.svg`,
      'fill-none': `${TL_ICON_PATHS}/fill-none.svg`,
      'fill-semi': `${TL_ICON_PATHS}/fill-semi.svg`,
      'fill-solid': `${TL_ICON_PATHS}/fill-solid.svg`,
      'fill-pattern': `${TL_ICON_PATHS}/fill-pattern.svg`,
      'dash-draw': `${TL_ICON_PATHS}/dash-draw.svg`,
      'dash-dashed': `${TL_ICON_PATHS}/dash-dashed.svg`,
      'dash-dotted': `${TL_ICON_PATHS}/dash-dotted.svg`,
      'dash-solid': `${TL_ICON_PATHS}/dash-solid.svg`,
      'size-small': `${TL_ICON_PATHS}/size-small.svg`,
      'size-medium': `${TL_ICON_PATHS}/size-medium.svg`,
      'size-large': `${TL_ICON_PATHS}/size-large.svg`,
      'size-extra-large': `${TL_ICON_PATHS}/size-extra-large.svg`,
      'font-draw': `${TL_ICON_PATHS}/font-draw.svg`,
      'font-sans': `${TL_ICON_PATHS}/font-sans.svg`,
      'font-serif': `${TL_ICON_PATHS}/font-serif.svg`,
      'font-mono': `${TL_ICON_PATHS}/font-mono.svg`,
      'text-align-left': `${TL_ICON_PATHS}/text-align-left.svg`,
      'text-align-center': `${TL_ICON_PATHS}/text-align-center.svg`,
      'text-align-right': `${TL_ICON_PATHS}/text-align-right.svg`,
      'vertical-align-center': `${TL_ICON_PATHS}/vertical-align-center.svg`,
      'vertical-align-start': `${TL_ICON_PATHS}/vertical-align-start.svg`,
      'vertical-align-end': `${TL_ICON_PATHS}/vertical-align-end.svg`,
    },
  };
};


/**
 * Compress an image using canvas
 * @param {HTMLImageElement} img - The loaded image
 * @param {number} w - Target width
 * @param {number} h - Target height
 * @param {string} fileType - Original file MIME type
 * @param {string} src - Original src (fallback if no compression)
 * @param {number} [quality=0.9] - Compression quality (0–1) for lossy formats
 * @returns {string} - Compressed DataURL
 */
// const compressImage = (img, w, h, fileType, fileSize, src, quality = 0.9) => {
//   return new Promise((resolve) => {
//     // If file size is <= 1MB, return original src
//     if (fileSize <= 1024 * 1024) {
//       resolve({
//         compressedSrc: src,
//         mimeType: fileType,
//       });
//       return;
//     }

//     // Otherwise, compress
//     const canvas = document.createElement("canvas");
//     canvas.width = w;
//     canvas.height = h;
//     const ctx = canvas.getContext("2d");
//     ctx.drawImage(img, 0, 0, w, h);

//     let compressedSrc;
//     let mimeType = fileType;

//     if (fileType === "image/jpeg" || fileType === "image/jpg") {
//       compressedSrc = canvas.toDataURL("image/jpeg");
//       mimeType = "image/jpeg";
//     } else if (fileType === "image/webp") {
//       compressedSrc = canvas.toDataURL("image/webp", 1.0); // lossless WebP
//       mimeType = "image/webp";
//     } else if (fileType === "image/png") {
//       compressedSrc = canvas.toDataURL("image/png");
//       mimeType = "image/png";
//     } else {
//       // fallback → original
//       compressedSrc = src;
//     }

//     resolve({ compressedSrc, mimeType });
//   });
// };



const compressImage = (src, fileType, fileSize, quality = 0.9, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve, reject) => {
    // Return original if <= 1MB
    // if (fileSize <= 100) {
    //   resolve({ compressedSrc: src, mimeType: fileType });
    //   return;
    // }
    // Convert src to a File or Blob
    fetch(src)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "image", { type: fileType });

        // Compress with Compressor.js
        new Compressor(file, {
          quality, // 0.85–0.9 recommended for text readability
          maxWidth,
          maxHeight,
          convertSize: 1024 * 1024, // compress all large images
          success(result) {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({ compressedSrc: reader.result, mimeType: result.type });
            };
            reader.onerror = reject;
            reader.readAsDataURL(result);
          },
          error(err) {
            reject(err);
          },
        });
      })
      .catch(err => reject(err));
  });
};



export {
  initDefaultPages,
  sendAnnotation,
  persistShape,
  notifyNotAllowedChange,
  notifyShapeNumberExceeded,
  notifyShapeNumberWarning,
  toggleToolsAnimations,
  formatAnnotations,
  getCustomEditorAssetUrls,
  getCustomAssetUrls,
  handleFiledrop,
  handleSavePresentation,
  compressImage
};
